import React, { memo, useEffect, useMemo, useState, useCallback } from "react";

// Sidebar
import Dock from "./Dock";
import BottomDock from "./BottomDock";
import { BottomAlertList } from "orion-components/SharedComponents";

// Notification Item
import NewNotificationItem from "./shared/components/NewNotificationItem";

import Drawer from "@mui/material/Drawer";
import Badge from "@mui/material/Badge";
import SvgIcon from "@mui/material/SvgIcon";
import Tooltip from "@mui/material/Tooltip";

// material icons
import InfoIcon from "@mui/icons-material/Info";
import { Videocam, Image, Phone } from "@mui/icons-material";

import { ClipboardPulse } from "mdi-material-ui";
import { Alert, Bell } from "orion-components/CBComponents/Icons";
import { makeStyles } from "@mui/styles";

// custom theme
import { StyledEngineProvider, ThemeProvider, createTheme } from "@mui/material/styles";
import customTheme from "./customTheme";
import { Dialog, NotificationsDialog } from "orion-components/CBComponents";

import { Translate, getTranslation } from "orion-components/i18n";
import { integrationService, unitService } from "client-app-core";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { startNotificationStream } from "./NotificationsCenter/notifications/actions";
import { startReminderStream } from "./NotificationsCenter/reminders/actions";
import { startAlertStream } from "./Alerts/actions";
import { getAllCameras } from "./Cameras/actions";
import { userCamerasSelector } from "./Cameras/selectors";
import { acknowledgeSystemNotification, clearSystemNotifications } from "../Services/SystemNotificationService/actions";
import { userIntegrationsOfEntityTypeSelector } from "orion-components/Session/Selectors";
import { getDir } from "orion-components/i18n/Config/selector";
import * as actionCreators from "./actions";
import { mdiCheckboxMultipleMarked, mdiShieldCheck, mdiAccountCircle } from "@mdi/js";
import find from "lodash/find";
import { setLocalAppState } from "orion-components/AppState/Actions";
import { alertNotificationsSelector, activeNotificationsSelector } from "orion-components/GlobalData/Selectors";
import { subscribeUnitMembers, subscribeUnits } from "orion-components/GlobalData/Actions";
import VehicleLPR from "../CBComponents/Icons/VehicleLPR";
import { EvacuationIcon } from "../CBComponents/Icons";

export const DockWrapperContext = React.createContext();

const useStyles = makeStyles(dir => ({
	badge: {
		width: 20,
		height: 20,
		padding: "unset",
		minHeight: "unset",
		maxHeight: "unset",
		minWidth: "12px",
		top: "4px",
		...(dir === "rtl" ? { left: "5px" } : { right: "5px" })
	}
}));

const DockWrapper = props => {
	const dispatch = useDispatch();

	const {
		shouldStreamCameras,
		shouldStreamNotifications,
		bottomAlertListEnabled,
		disableNotificationPanel,
		forReplay
	} = props;

	const {
		setTab,
		toggleOpen,
		getAppState,
		confirmFirstUse,
		confirmFailover,
		clearNotification,
		logOut,
		toggleWavCam
	} = actionCreators;

	const dockData = useSelector(state => state.appState.dock.dockData); //TODO: dockData.newAlerts
	const notifications = useSelector(alertNotificationsSelector, shallowEqual);
	const activeNotifications = useSelector(activeNotificationsSelector, shallowEqual);

	const cameraIntegrations = useSelector(
		state => userIntegrationsOfEntityTypeSelector("camera")(state),
		shallowEqual
	);
	const userCameras = useSelector(state => userCamerasSelector(state));
	const appId = useSelector(state => state.application.appId);
	const user = useSelector(state => state.session.user);
	const { profile, firstUseText, sessionEnded, loggedOutManually } = user;
	const { firstUseAck, id } = profile;
	const hasSysHealthError = useSelector(state => state.systemHealth.hasHealthError);
	const failoverDialogOpen = useSelector(state => state.systemHealth.failoverDialog.open);
	const externalSystems = useSelector(
		state => (state.session && state.session.organization && state.session.organization.externalSystems) || []
	);
	const componentState = dockData;

	const userHasCameras = cameraIntegrations && cameraIntegrations.length > 0;
	const userId = id;
	const timeFormatPreference = useSelector(state => state.appState.global.timeFormat);
	const systemNotifications = useSelector(state => state.systemNotifications);
	const dir = useSelector(state => getDir(state));
	const { applications } = profile;
	const evacuationApp = find(applications, { appId: "evacuation-accountability-app" });
	const unitsApp = find(applications, { appId: "units-app" });
	const statusBoardApp = find(applications, { appId: "status-board-app" });
	const lprApp = find(applications, { appId: "lpr-app" });
	const { alertPanelDockedBottom } = useSelector(state => state.appState?.persisted);
	const isBottomDock = bottomAlertListEnabled && alertPanelDockedBottom;
	const disableDock = (isBottomDock || disableNotificationPanel) && componentState?.tab === "Notifications";
	const selectedTab = useSelector(state => state.appState.persisted?.selectedTab);
	const bottomDockOpen = componentState.isOpen && isBottomDock && selectedTab === "Notifications";

	const mql = window.matchMedia("(max-width: 750px)");
	const classes = useStyles(dir);

	const [panelWidth, setPanelWidth] = useState(mql.matches ? "100%" : "420px");
	const [zetronPhoneVisible, setZetronPhoneVisible] = useState(false);
	const [unitSettings, setUnitSettings] = useState([]);

	const handlePanelWidth = hide => {
		if (hide) {
			setPanelWidth("0px");
		} else {
			setPanelWidth(mql.matches ? "100%" : "420px");
		}
	};
	const handleCustomPanelWidth = width => {
		setPanelWidth(width);
	};

	const handleTouchTap = tab => {
		dispatch(setTab(tab));
		dispatch(toggleOpen());
	};

	const handleAlertTouchTap = tab => {
		// document.getElementById("notification-tab-wrapper").scrollTop = 0;
		dispatch(setTab(tab));
		dispatch(toggleOpen());

		setTimeout(() => {
			const container = document.getElementById("notification-tab-wrapper");
			const target = document.getElementById("first-priority");
			scrollTo(container, target, 300);
		}, 1000);
	};

	const scrollTo = (element, destinationElement, duration) => {
		// This only works if scrolling from scrollTop 0 atm
		if (element && destinationElement && duration) {
			const destination = destinationElement.offsetTop - 80;
			const distance = destination - element.scrollTop;

			const easeOutQuad = t => {
				return t * (2 - t);
			};

			let time = 0;
			while (time < duration) {
				(function (time) {
					setTimeout(() => {
						const tValue = time / duration;
						const transformedT = easeOutQuad(tValue);
						element.scrollTop = distance * transformedT;
					}, time);
				})((time += 10));
			}
		}
	};

	const handleClose = useCallback(() => {
		if (isBottomDock) {
			dispatch(setTab("Notifications"));
		} else {
			dispatch(toggleOpen());
		}
	}, [dispatch, isBottomDock, setTab, toggleOpen]);

	useEffect(() => {
		if (!componentState.isOpen) {
			setPanelWidth(mql.matches ? "100%" : "420px");
		}
	}, [componentState.isOpen, mql.matches]);

	useEffect(() => {
		dispatch(setLocalAppState("isBottomDock", bottomDockOpen));
	}, [bottomDockOpen]);

	useEffect(() => {
		dispatch(getAppState());
		if (shouldStreamNotifications) {
			dispatch(startNotificationStream());
			dispatch(startReminderStream());
			dispatch(startAlertStream());
		}
		if (shouldStreamCameras) {
			dispatch(getAllCameras());
		}

		const serviceCallBack = (err, response) => {
			if (err) {
				if (!zetronPhoneVisible) {
					setZetronPhoneVisible(false);
				}
			} else {
				if (response.clientInstalled) {
					setZetronPhoneVisible(true);
				}
			}
		};

		if (externalSystems) {
			if (externalSystems && externalSystems.length > 0) {
				const flagTmp = externalSystems.includes("zetron");
				if (flagTmp) {
					integrationService.getExternalSystemLookup("zetron", "serviceAvailableToUser", serviceCallBack);
				}
			}
		}

		// Listen for changes in screen size and adjust width accordingly
		const mql = window.matchMedia("(max-width: 750px)");
		mql.addListener(e => {
			if (e.matches) {
				setPanelWidth("100%");
			} else {
				setPanelWidth(420);
			}
		});
	}, []);

	useEffect(() => {
		if (getUnitPanel(appId) && unitsApp) {
			dispatch(subscribeUnitMembers());
			dispatch(subscribeUnits());
			unitService.getAppSettingsByKey("units-app", "unitMemberFeeds", (err, response) => {
				if (err) {
					console.log("ERROR:", err);
				} else {
					setUnitSettings(response.value);
				}
			});
		}
	}, [appId, unitsApp]);

	const getUnitPanel = appName => {
		switch (appName) {
			case "map-app":
				return true;

			default:
				return false;
		}
	};

	const handleConfirmFirstUse = () => {
		dispatch(confirmFirstUse(userId));
	};

	const handleConfirmFailover = () => {
		dispatch(confirmFailover());
	};

	const openNotifications = useMemo(() => notifications.filter(item => !item?.closed), [notifications]);

	const alerts = useMemo(
		() =>
			openNotifications.filter(item => {
				return item.isPriority;
			}),
		[openNotifications]
	);

	const totalAlertOccurrences = useMemo(
		() => alerts.reduce((sum, alert) => sum + (alert.occurrenceCount || 1), 0),
		[alerts]
	);

	const unacknowledgedAlerts = alerts?.filter(alert => alert.occurrenceCount !== alert.ackedCount);

	const nonPriority = useMemo(
		() =>
			openNotifications.filter(item => {
				return !item.isPriority;
			}),
		[openNotifications]
	);

	const userHasWavCameras = useMemo(
		() =>
			userCameras.some(
				cam => cam.entityData.properties.features && cam.entityData.properties.features.includes("ribbon")
			),
		[userCameras]
	);

	const dialogNotifications = systemNotifications
		.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
		.map(notification => {
			const { id, title, content, forceRefresh } = notification;
			return {
				title,
				textContent: content,
				confirm: {
					label: getTranslation(
						`global.CBComponents.notificationsDialog.${forceRefresh ? "refresh" : "acknowledge"}`
					),
					action: () => {
						dispatch(acknowledgeSystemNotification(id));

						if (forceRefresh) {
							location.reload();
						}
					}
				}
			};
		});

	const handleNotificationTab = alertTouchTap => {
		if (disableNotificationPanel) return;

		if (componentState?.isOpen) {
			dispatch(setTab("Notifications"));
		} else {
			alertTouchTap ? handleAlertTouchTap("Notifications") : handleTouchTap("Notifications");
		}
		dispatch(setLocalAppState("selectedTab", "Notifications"));
	};

	// Docked prop on drawer allows it to be closed via click-away
	return (
		<StyledEngineProvider injectFirst>
			<ThemeProvider theme={createTheme(customTheme)}>
				<div className={dir && dir === "rtl" ? "alert-sidebar-trayRTL" : "alert-sidebar-tray"}>
					<Dialog
						open={sessionEnded && !loggedOutManually}
						title={getTranslation("global.dock.dockWrapper.sessionExpired")}
						textContent={getTranslation("global.dock.dockWrapper.redirectLogin")}
						confirm={{
							label: getTranslation("global.dock.dockWrapper.ok"),
							action: () => dispatch(logOut())
						}}
						dir={dir}
						dialogContentStyles={{
							color: "#fff"
						}}
					/>
					<Dialog
						open={!firstUseAck}
						textContent={firstUseText}
						title={getTranslation("global.dock.dockWrapper.userAgreement")}
						confirm={{
							label: getTranslation("global.dock.dockWrapper.accept"),
							action: handleConfirmFirstUse
						}}
						abort={{
							label: getTranslation("global.dock.dockWrapper.decline"),
							action: () => dispatch(logOut())
						}}
						dir={dir}
					/>
					<Dialog
						open={failoverDialogOpen}
						textContent={getTranslation("global.dock.dockWrapper.failoverContent")}
						title={getTranslation("global.dock.dockWrapper.failoverTitle")}
						confirm={{
							label: getTranslation("global.dock.dockWrapper.ok"),
							action: handleConfirmFailover
						}}
						dir={dir}
					/>

					<Tooltip title={getTranslation("global.dock.onlineUsers.title")}>
						<a
							className="link-wrapper"
							onClick={() => {
								if (componentState.isOpen) {
									dispatch(setTab("Online_Users"));
								} else {
									handleTouchTap("Online_Users");
								}
							}}
						>
							<SvgIcon
								style={{
									width: "24px",
									height: "24px",
									color: "#FFFFFF"
								}}
							>
								<path d={mdiAccountCircle} />
							</SvgIcon>
						</a>
					</Tooltip>

					{!sessionEnded &&
						firstUseAck && ( // -- don't show notifications if firstUseAck dialog or sessionEnded dialog open
							<NotificationsDialog
								notifications={dialogNotifications}
								clearSystemNotifications={clearSystemNotifications}
								dir={dir}
							/>
						)}

					{getUnitPanel(appId) && unitsApp && unitsApp.permissions.includes("manage") && (
						<Tooltip title={getTranslation("global.dock.unitsPanel.title")}>
							<a
								className="link-wrapper"
								onClick={
									componentState.isOpen
										? () => dispatch(setTab("Units_panel"))
										: () => handleTouchTap("Units_panel")
								}
							>
								<SvgIcon
									style={{
										width: "24px",
										height: "24px",
										color: "#FFFFFF"
									}}
								>
									<path d={mdiShieldCheck} />
								</SvgIcon>
							</a>
						</Tooltip>
					)}
					{externalSystems?.includes("veer-lpr") && (
						<Tooltip title={getTranslation("global.dock.lprManager.title")}>
							<a
								className="link-wrapper"
								onClick={() => {
									if (componentState.isOpen) {
										dispatch(setTab("LPR_Manager"));
									} else {
										handleTouchTap("LPR_Manager");
									}
								}}
							>
								<VehicleLPR />
							</a>
						</Tooltip>
					)}
					{lprApp && (
						<Tooltip title={getTranslation("global.dock.LPRWatchlistPanel.title")}>
							<a
								className="link-wrapper"
								onClick={() => {
									if (componentState.isOpen) {
										dispatch(setTab("LPR_Watchlist_Panel"));
									} else {
										handleTouchTap("LPR_Watchlist_Panel");
									}
								}}
							>
								<VehicleLPR />
							</a>
						</Tooltip>
					)}
					{statusBoardApp && (
						<Tooltip title={getTranslation("global.dock.statusBoard.title")}>
							<a
								className="link-wrapper"
								onClick={
									componentState.isOpen
										? () => dispatch(setTab("Status_board"))
										: () => handleTouchTap("Status_board")
								}
							>
								<SvgIcon
									style={{
										width: "24px",
										height: "24px",
										color: "#FFFFFF"
									}}
								>
									<path d={mdiCheckboxMultipleMarked} />
								</SvgIcon>
							</a>
						</Tooltip>
					)}

					{zetronPhoneVisible && (
						<Tooltip title={getTranslation("global.dock.zetronCallingPanel.title")}>
							<a
								className="link-wrapper"
								onClick={
									componentState.isOpen
										? () => dispatch(setTab("Calling_Panel"))
										: () => handleTouchTap("Calling_Panel")
								}
							>
								<Phone
									style={{
										width: "24px",
										height: "24px",
										color: "#FFFFFF"
									}}
								/>
							</a>
						</Tooltip>
					)}

					{alerts.length > 0 && <div className="alert-bar-overlay" />}
					{alerts.length > 0 && (
						<a className="link-wrapper alert-wrapper" onClick={() => handleNotificationTab(true)}>
							<div
								className="tray-icon alert-icon pulse bounce"
								style={!unacknowledgedAlerts.length ? { animation: "none" } : {}}
							>
								<div className="item-info large">{totalAlertOccurrences}</div>
							</div>
							<span>
								<Translate value="global.dock.dockWrapper.alarms" />
							</span>
						</a>
					)}
					<Tooltip title={getTranslation("global.dock.systemHealth.title")}>
						<a
							className="link-wrapper"
							onClick={
								componentState.isOpen
									? () => dispatch(setTab("System_health"))
									: () => handleTouchTap("System_health")
							}
						>
							{hasSysHealthError ? (
								<Badge
									badgeContent={
										<Alert
											iconStyles={{
												marginTop: "6px",
												fontSize: "20px"
											}}
											iconColor="#C64849"
										/>
									}
								>
									<ClipboardPulse className="dockWrapperCommonIcons" />
								</Badge>
							) : (
								<ClipboardPulse className="dockWrapperCommonIcons" />
							)}
						</a>
					</Tooltip>
					<Tooltip title={getTranslation("global.dock.notifications.main.title")}>
						<a className="link-wrapper" onClick={() => handleNotificationTab()}>
							{nonPriority.length > 0 ? (
								<Badge
									badgeContent={
										<Alert
											iconStyles={{
												marginTop: "6px",
												fontSize: "20px"
											}}
											iconColor="#C64849"
										/>
									}
								>
									<InfoIcon className="dockWrapperCommonIcons" />
								</Badge>
							) : (
								<InfoIcon className="dockWrapperCommonIcons" />
							)}
						</a>
					</Tooltip>
					<Tooltip title={getTranslation("global.dock.notificationsCenter.title")}>
						<a
							className="link-wrapper"
							onClick={
								componentState.isOpen
									? () => dispatch(setTab("Notifications_center"))
									: () => handleTouchTap("Notifications_center")
							}
						>
							{activeNotifications.length > 0 ? (
								<Badge
									classes={{ badge: classes.badge }}
									badgeContent={
										<div className="tray-icon alert-icon pulse bounce">
											<div className="item-info" style={styles.notificationsCount}>
												{activeNotifications.length}
											</div>
										</div>
									}
								>
									<Bell
										customStyle={{
											width: "24px",
											height: "24px",
											color: "#FFF"
										}}
									/>
								</Badge>
							) : (
								<Bell
									customStyle={{
										width: "24px",
										height: "24px",
										color: "#FFF"
									}}
								/>
							)}
						</a>
					</Tooltip>
					{userHasWavCameras && (
						<Tooltip title={getTranslation("global.dock.wavCam.title")}>
							<a className="link-wrapper" onClick={() => dispatch(toggleWavCam())}>
								<Image
									style={{
										color: "white",
										width: "24px",
										height: "24px"
									}}
								/>
							</a>
						</Tooltip>
					)}
					{userHasCameras && (
						<Tooltip title={getTranslation("global.dock.cameras.title")}>
							<a
								className="link-wrapper"
								onClick={
									componentState.isOpen
										? () => dispatch(setTab("Cameras"))
										: () => handleTouchTap("Cameras")
								}
							>
								<Videocam className="dockWrapperCommonIcons" />
							</a>
						</Tooltip>
					)}
					{evacuationApp && (
						<Tooltip title={getTranslation("global.dock.Evacuation.title")}>
							<a
								className="link-wrapper"
								onClick={() => {
									if (componentState.isOpen) {
										dispatch(setTab("Evacuation"));
									} else {
										handleTouchTap("Evacuation");
									}
								}}
							>
								<EvacuationIcon />
							</a>
						</Tooltip>
					)}

					<div className="new-alerts">
						{componentState.newAlerts.length > 0 &&
							componentState.newAlerts.map(item => (
								<div key={item.id}>
									<NewNotificationItem
										notification={item}
										key={item.id}
										isLonely={componentState.newAlerts.length === 1}
										clearNotification={clearNotification}
										timeFormatPreference={timeFormatPreference ? timeFormatPreference : "12-hour"}
										dir={dir}
									/>
								</div>
							))}
					</div>
					<Drawer
						open={componentState.isOpen && !disableDock}
						anchor={dir === "rtl" ? "left" : "right"}
						sx={{
							["& .MuiDrawer-paper"]: {
								width: componentState.isOpen ? panelWidth : " 420px",
								backgroundColor: "#2C2B2D",
								position: "fixed!important",
								height: "calc(100vh - 48px) !important",
								top: "48px !important",
								overflow: " visible !important",
								zIndex: "2003 !important"
							}
						}}
						PaperProps={{ sx: { zIndex: 2400 } }}
						variant="persistent"
					>
						<div onClick={() => dispatch(toggleOpen())} className="ad-toggle-mobile" />
						{componentState.isOpen && !disableDock && (
							<DockWrapperContext.Provider value={{ handlePanelWidth, handleCustomPanelWidth }}>
								<Dock
									{...props}
									dir={dir}
									setTab={setTab}
									componentState={componentState}
									notifications={notifications}
									unitSettings={unitSettings}
									statusBoardApp={statusBoardApp}
									handleClose={handleClose}
									forReplay={forReplay}
								/>
							</DockWrapperContext.Provider>
						)}
					</Drawer>
					<BottomDock
						open={bottomDockOpen}
						dockTitle={getTranslation("global.dock.notifications.main.title")}
						notifications={notifications}
						bottomAlertListEnabled={bottomAlertListEnabled}
						disableNotificationPanel={disableNotificationPanel}
					>
						<BottomAlertList
							notifications={notifications}
							readOnly={props.readOnly}
							isBottomDock={isBottomDock}
						/>
					</BottomDock>
				</div>
			</ThemeProvider>
		</StyledEngineProvider>
	);
};

DockWrapper.defaultProps = {
	shouldStreamNotifications: true
};

//const shouldComponentUpdate = (prevProps, nextProps) => {
//	return (
//		isEqual(nextProps, prevProps)
//	);
//}

export default memo(DockWrapper);
